import React, { useState, useEffect } from "react";
import { useNavigate, useParams } from "react-router-dom";
import styles from "./artistBooking.module.css";
import { useDispatch, useSelector } from "react-redux";
import { initiateBooking } from "../../redux/slices/bookingSlice";
import { fetchArtistPackage } from "../../redux/slices/packageSlice";
import RazorPayGateway from "../../components/RazorPayGateway";
import { getArtistByUsername } from "../../redux/slices/artistSlice";
import HowItWorksUser from "../../components/HowItWorksUser";

const ArtistBooking = () => {
  const { artistUsername } = useParams();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [selectedPackage, setSelectedPackage] = useState(null);
  const [selectedDate, setSelectedDate] = useState("");
  const [selectedTime, setSelectedTime] = useState("");
  const [location, setLocation] = useState("");
  const [pinCode, setPinCode] = useState(null);
  const [city, setCity] = useState("");
  const [state, setState] = useState("");
  const [eventDetails, setEventDetails] = useState("");
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [formErrors, setFormErrors] = useState({});
  const [openRazorPay, setOpenRazorPay] = useState(false);
  const [showWorkflowModal, setShowWorkflowModal] = useState(false);
  const [address, setAddress] = useState("");
  const dispatch = useDispatch();
  const { artistByUsername } = useSelector((state) => state.artists);
  const { initiateBookingPayload } = useSelector((state) => state.booking);

  const { packages } = useSelector((state) => state.package);

  useEffect(() => {
    if (artistUsername) {
      dispatch(fetchArtistPackage({ artistUsername }));
    }
  }, [artistUsername]);

  useEffect(() => {
    const fetchArtist = async () => {
      try {
        dispatch(getArtistByUsername({ username: artistUsername }));
        setLoading(false);
      } catch (error) {
        console.error("Error fetching artist data:", error);
        setLoading(false);
      }
    };

    fetchArtist();
  }, [artistUsername]);

  const handlePackageSelect = (pkg) => {
    setSelectedPackage(pkg);
  };

  const handleDateChange = (e) => {
    setSelectedDate(e.target.value);
  };

  const handleTimeChange = (e) => {
    setSelectedTime(e.target.value);
  };

  const validateForm = () => {
    const errors = {};

    if (!selectedDate) errors.date = "Please select a date";
    if (!selectedTime) errors.time = "Please select a time";
    if (!selectedPackage) errors.package = "Please select a package";
    if (!city) errors.city = "Please enter City";
    if (!state) errors.state = "Please enter State";
    if (!location) errors.location = "Please enter Address";

    setFormErrors(errors);
    return Object.keys(errors).length === 0;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!validateForm()) return;

    setIsSubmitting(true);

    const res = await dispatch(
      initiateBooking({
        bookingDate: selectedDate,
        duration: selectedPackage?.duration,
        details: eventDetails,
        address: location,
        pincode: pinCode,
        state: state,
        city: city,
        amount: selectedPackage?.price,
        currency: "INR",
        packageId: selectedPackage?._id,
        artistUsername: artistUsername,
        consentIds: [1],
      })
    );

    if (res) {
      setOpenRazorPay(true);
    }
  };

  if (loading) {
    return (
      <div className={styles.loadingContainer}>
        <div className={styles.loadingSpinner}></div>
        <p>Loading artist information...</p>
      </div>
    );
  }

  if (!artistByUsername) {
    return (
      <div className={styles.errorContainer}>
        <h2>Artist Not Found</h2>
        <p>We couldn't find the artist you're looking for.</p>
        <button onClick={() => navigate("/")} className={styles.returnButton}>
          Return to Home
        </button>
      </div>
    );
  }

  return (
    <div className={styles.bookingPage}>
      <div
        className={styles.artistHeader}
        style={{ backgroundImage: `url(${artistByUsername?.coverImage})` }}
      >
        <div className={styles.artistHeaderOverlay}>
          <div className={styles.artistInfo}>
            <img
              src={artistByUsername?.displayPictureUrl}
              alt={artistByUsername?.first_name}
              className={styles.artistImage}
            />
            <div className={styles.artistDetails}>
              <h1>{`${
                artistByUsername?.first_name !== undefined
                  ? artistByUsername?.first_name
                  : ""
              } ${
                artistByUsername?.last_name ? artistByUsername?.last_name : ""
              }`}</h1>
              <p className={styles.artistType}>
                {artistByUsername?.username} -{" "}
                {artistByUsername?.categories
                  ?.map((category) => category.name)
                  .join(", ")}
              </p>
              <div className={styles.artistRating}>
                <span className={styles.ratingStars}>
                  {"★".repeat(Math.floor(artistByUsername?.rating))}
                  {"☆".repeat(5 - Math.floor(artistByUsername?.rating))}
                </span>
                <span className={styles.ratingValue}>
                  {artistByUsername?.rating}
                </span>
                <span className={styles.reviewCount}>
                  ({artistByUsername?.reviews} reviews)
                </span>
              </div>
            </div>
          </div>
        </div>
      </div>

      <div className={styles.bookingContainer}>
        <div className={styles.bookingFormContainer}>
          <div className={styles.bookingTitleContainer}>
            <h2 className={styles.bookingTitle}>
              Book {artistByUsername?.name}
            </h2>
            <button
              className={styles.howItWorksButton}
              onClick={() => setShowWorkflowModal(true)}
            >
              How Bookings Work
            </button>
          </div>

          <form onSubmit={handleSubmit} className={styles.bookingForm}>
            <div className={styles.packageSection}>
              <h3>Select a Package</h3>
              <div className={styles.packagesGrid}>
                {packages?.map((pkg) => (
                  <div
                    key={pkg.id}
                    className={`${styles.packageCard} ${
                      selectedPackage?._id === pkg._id
                        ? styles.selectedPackage
                        : ""
                    }`}
                    onClick={() => handlePackageSelect(pkg)}
                  >
                    <div className={styles.packageHeader}>
                      <h4>{pkg.packageName}</h4>
                      <span className={styles.packagePrice}>₹{pkg.price}</span>
                    </div>
                    <p className={styles.packageDuration}>
                      {pkg.duration} hours
                    </p>
                    <strong>
                      <p className={styles.packageDescription}>
                        {pkg.description}
                      </p>
                    </strong>
                    <ul className={styles.packageFeatures}>
                      {pkg.details.map((feature, index) => (
                        <li key={index}>{feature}</li>
                      ))}
                    </ul>
                    <div className={styles.packageSelect}>
                      {selectedPackage?._id === pkg._id ? "Selected" : "Select"}
                    </div>
                  </div>
                ))}
              </div>
              {formErrors.package && (
                <p className={styles.errorText}>{formErrors.package}</p>
              )}
            </div>

            <div className={styles.dateTimeSection}>
              <h3>Select Date & Time</h3>
              <div className={styles.dateTimePicker}>
                <div className={styles.inputGroup}>
                  <label htmlFor="date">Date</label>
                  <input
                    type="date"
                    id="date"
                    value={selectedDate}
                    onChange={handleDateChange}
                    min={new Date().toISOString().split("T")[0]}
                    className={styles.dateInput}
                  />
                  {formErrors.date && (
                    <p className={styles.errorText}>{formErrors.date}</p>
                  )}
                </div>

                <div className={styles.inputGroup}>
                  <label htmlFor="time">Time</label>
                  <input
                    type="time"
                    id="time"
                    value={selectedTime}
                    onChange={handleTimeChange}
                    className={styles.timeInput}
                  />
                  {formErrors.time && (
                    <p className={styles.errorText}>{formErrors.time}</p>
                  )}
                </div>
              </div>
            </div>

            <div className={styles.locationSection}>
              <h3>Event Location</h3>
              <div className={styles.inputGroup}>
                <label htmlFor="pincode">Pin Code</label>
                <input
                  type="number"
                  id="pincode"
                  value={pinCode}
                  onChange={(e) => setPinCode(e.target.value)}
                  placeholder="Enter the event Pin code"
                  className={styles.locationInput}
                />
                {formErrors.pinCode && (
                  <p className={styles.errorText}>{formErrors.pincode}</p>
                )}
              </div>
              <div className={styles.inputGroup}>
                <label htmlFor="location">Address</label>
                <input
                  type="text"
                  id="location"
                  value={location}
                  onChange={(e) => setLocation(e.target.value)}
                  placeholder="Enter the event location"
                  className={styles.locationInput}
                />
                {formErrors.location && (
                  <p className={styles.errorText}>{formErrors.location}</p>
                )}
              </div>
              <div className={styles.inputGroup}>
                <label htmlFor="city">City</label>
                <input
                  type="text"
                  id="city"
                  value={city}
                  onChange={(e) => setCity(e.target.value)}
                  placeholder="Enter the event City"
                  className={styles.locationInput}
                />
                {formErrors.city && (
                  <p className={styles.errorText}>{formErrors.city}</p>
                )}
              </div>

              <div className={styles.inputGroup}>
                <label htmlFor="state">State</label>
                <input
                  type="text"
                  id="state"
                  value={state}
                  onChange={(e) => setState(e.target.value)}
                  placeholder="Enter the event State"
                  className={styles.locationInput}
                />
                {formErrors.state && (
                  <p className={styles.errorText}>{formErrors.state}</p>
                )}
              </div>
            </div>

            <div className={styles.detailsSection}>
              <h3>Event Details</h3>
              <div className={styles.inputGroup}>
                <label htmlFor="details">Additional Information</label>
                <textarea
                  id="details"
                  value={eventDetails}
                  onChange={(e) => setEventDetails(e.target.value)}
                  placeholder="Share any additional information about your event"
                  className={styles.detailsInputTextArea}
                  rows="4"
                />
              </div>
            </div>

            <div className={styles.bookingSummary}>
              <h3>Booking Summary</h3>
              <div className={styles.summaryContent}>
                <div className={styles.summaryItem}>
                  <span>Artist:</span>
                  <span>{artistByUsername?.first_name}</span>
                </div>

                {selectedPackage && (
                  <div className={styles.summaryItem}>
                    <span>Package:</span>
                    <span>
                      {selectedPackage.packageName} ({selectedPackage.duration}{" "}
                      hours)
                    </span>
                  </div>
                )}

                {selectedDate && (
                  <div className={styles.summaryItem}>
                    <span>Date:</span>
                    <span>
                      {new Date(selectedDate).toLocaleDateString("en-US", {
                        weekday: "long",
                        year: "numeric",
                        month: "long",
                        day: "numeric",
                      })}
                    </span>
                  </div>
                )}

                {selectedTime && (
                  <div className={styles.summaryItem}>
                    <span>Time:</span>
                    <span>{selectedTime}</span>
                  </div>
                )}

                {selectedPackage && (
                  <div className={`${styles.summaryItem} ${styles.totalItem}`}>
                    <span>Total:</span>
                    <span>₹{selectedPackage.price}</span>
                  </div>
                )}
              </div>
            </div>

            <div className={styles.bookingNotice}>
              <div className={styles.noticeIcon}>ℹ️</div>
              <div className={styles.noticeText}>
                <p>
                  After booking, the artist has 24 hours to accept your request.
                  Once accepted, you'll need to complete payment within 3 days.
                </p>
                <button
                  className={styles.learnMoreLink}
                  type="button"
                  onClick={() => setShowWorkflowModal(true)}
                >
                  Learn more about how bookings work
                </button>
              </div>
            </div>

            <button
              type="submit"
              className={styles.bookButton}
              disabled={isSubmitting}
            >
              {isSubmitting ? "Processing..." : "Confirm Booking"}
            </button>
          </form>
          {openRazorPay && (
            <RazorPayGateway orderId={initiateBookingPayload.orderId} />
          )}
        </div>

        <div className={styles.artistBioContainer}>
          <h2>About {artistByUsername?.first_name}</h2>
          <p className={styles.artistBio}>{artistByUsername?.about}</p>

          <div className={styles.bookingProcessCard}>
            <h3>How the Booking Process Works</h3>
            <ol className={styles.processList}>
              <li>
                <div className={styles.processStep}>
                  <div className={styles.stepNumber}>1</div>
                  <div className={styles.stepContent}>
                    <strong>Make a token payment</strong> to initiate your
                    booking request
                  </div>
                </div>
              </li>
              <li>
                <div className={styles.processStep}>
                  <div className={styles.stepNumber}>2</div>
                  <div className={styles.stepContent}>
                    <strong>Artist accepts</strong> within 24 hours
                  </div>
                </div>
              </li>
              <li>
                <div className={styles.processStep}>
                  <div className={styles.stepNumber}>3</div>
                  <div className={styles.stepContent}>
                    <strong>Complete full payment</strong> within 3 days to
                    confirm
                  </div>
                </div>
              </li>
              <li>
                <div className={styles.processStep}>
                  <div className={styles.stepNumber}>4</div>
                  <div className={styles.stepContent}>
                    <strong>Enjoy your event</strong> with the artist
                  </div>
                </div>
              </li>
            </ol>
            <button
              className={styles.viewDetailsButton}
              onClick={() => setShowWorkflowModal(true)}
            >
              View Full Details
            </button>
          </div>
        </div>
      </div>

      {/* User Booking Workflow Modal */}
      <HowItWorksUser
        isOpen={showWorkflowModal}
        onClose={() => setShowWorkflowModal(false)}
      />
    </div>
  );
};

export default ArtistBooking;
